import React, { useEffect, useState, useRef } from 'react';
import './Analysefaisabilite.css';

const API = window.location.hostname === 'localhost'
  ? 'http://localhost:5000/api'
  : 'https://demo.myssa.net/api';

// Colonnes du tableau 1 et colonnes masquées du tableau 2 (inchangées)
const HEADERS = ["Type", "Numéro d'offre", "Client", "Date demandée", "Date confirmée"];
const HIDDEN  = ["Analyse", "ID", "Client_id"];

// Colonnes numériques du détail (police mono ; centrées comme le reste, cf. <style> du rendu)
const COLONNES_NUM = ["Quantité demandée", "Quantité manquante", "Quantité disponible", "Stock"];

const Pill = ({ tone, children }) => <span className={`af-badge af-badge-${tone}`}>{children}</span>;

const TableauAnalyseFaisabilite = () => {
  const [data, setData]                     = useState([]);
  const [loading, setLoading]               = useState(true);
  const [selectedNumero, setSelectedNumero] = useState(null);
  const detailRef = useRef(null);

  // Clic sur une ligne : ouvre le détail de l'offre, ou le referme si elle est déjà ouverte
  const basculerOffre = (numero) => {
    setSelectedNumero(prev => (prev === numero ? null : numero));
  };

  // ★ À l'ouverture d'un détail, on fait défiler la page jusqu'au tableau 2
  useEffect(() => {
    if (selectedNumero && detailRef.current) {
      detailRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedNumero]);

  useEffect(() => {
    document.title = 'MYSSA – Analyse faisabilité';
  }, []);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/analyse_faisabilite`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(err => { console.error(err); setLoading(false); });
  }, []);

  // ── Zéro new Date() → zéro décalage UTC ──────────────────
  const formatDatePG = (val) => {
    if (!val) return '';
    // Cas 1 : déjà DD/MM/YYYY → on retourne tel quel
    if (typeof val === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(val)) return val;
    // Cas 2 : ISO "2026-05-09T00:00:00.000Z" ou "2026-05-09"
    if (typeof val === 'string') {
      const datePart = val.split('T')[0];
      const parts    = datePart.split('-');
      if (parts.length === 3) {
        const [year, month, day] = parts;
        return `${day}/${month}/${year}`;
      }
    }
    return '';
  };

  const firstLinePerOffre = Object.values(
    data.reduce((acc, item) => {
      if (!acc[item["Numéro d'offre"]]) acc[item["Numéro d'offre"]] = item;
      return acc;
    }, {})
  );

  const secondTableData = selectedNumero
    ? data.filter(item => item["Numéro d'offre"] === selectedNumero)
    : [];

  // Même règle que l'original : OK si date demandée = date confirmée
  const offreOK = (item) => {
    const dateDem = formatDatePG(item["Date demandée"]);
    const dateCon = formatDatePG(item["Date confirmée"]);
    return Boolean(dateDem && dateCon && dateDem === dateCon);
  };

  // Même règle que l'original : 0 manquant = OK, tout manquant = KO, sinon Partiel
  const toneManquant = (item) => {
    const qteDem = Math.abs(parseFloat(item["Quantité demandée"]));
    const qteMnq = Math.abs(parseFloat(item["Quantité manquante"]));
    if (isNaN(qteMnq) || isNaN(qteDem)) return null;
    if (qteMnq === 0) return 'ok';
    if (qteMnq === qteDem) return 'danger';
    return 'warn';
  };

  const nbOK = firstLinePerOffre.filter(offreOK).length;
  const nbKO = firstLinePerOffre.length - nbOK;

  const colonnesDetail = secondTableData.length
    ? Object.keys(secondTableData[0]).filter(h => !HIDDEN.includes(h))
    : [];

  const clientSel = secondTableData[0]?.["Client"];

  return (
    <div className="af-page">
      {/* ★ En-têtes et données centrés dans les 2 tableaux (prioritaire sur Analysefaisabilite.css,
            y compris la classe "num" qui aligne les quantités à droite) */}
      <style>{`
        .af-table th, .af-table th.num,
        .af-table td, .af-table td.num { text-align: center !important; vertical-align: middle; }
        /* ★ Lignes du tableau 1 cliquables */
        .af-row-clic { cursor: pointer; transition: filter .12s ease; }
        .af-row-clic:hover { filter: brightness(0.96); }
        .af-row-clic:focus-visible { outline: 2px solid #38bdf8; outline-offset: -2px; }
        .af-row-clic.af-row-active td { font-weight: 600; }
      `}</style>
      <div className="af-container">

        {/* ── En-tête ── */}
        <div className="af-header">
          <div className="af-header-left">
            <div className="af-title-row">
              <span className="af-accent" />
              <h1 className="af-title">Analyse de faisabilité</h1>
              <span className="af-chip-dept">DRC</span>
            </div>
        
          </div>

          {/* Légende */}
          <div className="af-legend">
            <Pill tone="ok">OK</Pill>
            <Pill tone="danger">KO</Pill>
            <Pill tone="warn">Partiel</Pill>
          </div>
        </div>

        {loading ? (
          <div className="af-card af-empty">Chargement…</div>
        ) : (
          <>
            {/* ── Tableau 1 : une ligne par offre ── */}
          <div className="af-card af-table-card" style={{ marginTop: '60px' }}>
              <div className="af-table-head">
                <div className="af-table-head-left">
                  <span className="af-table-title">Offres</span>
                  <span className="af-count">{firstLinePerOffre.length}</span>
                </div>
                <div className="af-table-head-right">
                  <span className="af-stat af-stat-ok">{nbOK} OK</span>
                  <span className="af-stat af-stat-danger">{nbKO} KO</span>
                </div>
              </div>

              <div className="af-table-scroll af-scroll-1">
                {firstLinePerOffre.length === 0 ? (
                  <div className="af-empty">Aucune offre à analyser.</div>
                ) : (
                  <table className="af-table">
                    <thead>
                      <tr>{HEADERS.map(h => <th key={h}>{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {firstLinePerOffre.map((item, index) => {
                        const ok     = offreOK(item);
                        const numero = item["Numéro d'offre"];
                        const actif  = selectedNumero === numero;
                        return (
                          <tr
                            key={index}
                            className={`af-row-clic af-row-${ok ? 'ok' : 'danger'} ${actif ? 'af-row-active' : ''}`}
                            onClick={() => basculerOffre(numero)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); basculerOffre(numero); }
                            }}
                            tabIndex={0}
                            role="button"
                            aria-expanded={actif}
                            title={actif ? 'Cliquer pour masquer le détail' : 'Cliquer pour afficher le détail'}
                          >
                            {HEADERS.map((header, idx) => {
                              if (header === "Numéro d'offre") {
                                return (
                                  <td key={idx}>
                                    <span className="af-link">{numero}</span>
                                  </td>
                                );
                              }
                              if (header === "Date demandée" || header === "Date confirmée") {
                                const d = formatDatePG(item[header]);
                                return (
                                  <td key={idx}>
                                    {d ? <Pill tone={ok ? 'ok' : 'danger'}>{d}</Pill> : <span className="af-muted">—</span>}
                                  </td>
                                );
                              }
                              if (header === "Client") {
                                return <td key={idx} className="af-strong">{item[header]}</td>;
                              }
                              return <td key={idx}>{item[header]}</td>;
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* ── Tableau 2 : détail de l'offre sélectionnée ── */}
            {selectedNumero && secondTableData.length > 0 && (
            <div ref={detailRef} className="af-card af-table-card" style={{ marginTop: '40px', scrollMarginTop: '20px' }}>
                <div className="af-table-head">
                  <div className="af-table-head-left">
                    <span className="af-table-title">Détail de l'offre</span>
                    <span className="af-offre-chip">{selectedNumero}</span>
                    {clientSel && <span className="af-client">{clientSel}</span>}
                    <span className="af-count">{secondTableData.length} ligne{secondTableData.length > 1 ? 's' : ''}</span>
                  </div>
                  <div className="af-table-head-right">
                    <button type="button" className="af-btn af-btn-ghost" onClick={() => setSelectedNumero(null)}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
                      Fermer
                    </button>
                  </div>
                </div>

                <div className="af-table-scroll af-scroll-2">
                  <table className="af-table">
                    <thead>
                      <tr>
                        {colonnesDetail.map(h => (
                          <th key={h} className={COLONNES_NUM.includes(h) ? 'num' : ''}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {secondTableData.map((item, index) => {
                        const tone = toneManquant(item);
                        return (
                          <tr key={index} className={tone ? `af-row-${tone}` : ''}>
                            {colonnesDetail.map((header, idx) => {
                              if (header === "Quantité manquante") {
                                return (
                                  <td key={idx} className="num">
                                    {tone ? <Pill tone={tone}>{item[header]}</Pill> : item[header]}
                                  </td>
                                );
                              }
                              if (header === "Date de disponibilité") {
                                const complet = parseFloat(item["Quantité manquante"]) === 0;
                                const d = formatDatePG(item[header]);
                                return (
                                  <td key={idx}>
                                    {complet
                                      ? <span className="af-muted">—</span>
                                      : d ? <Pill tone="danger">{d}</Pill> : <span className="af-muted">—</span>}
                                  </td>
                                );
                              }
                              if (header === "Date demandée" || header === "Date confirmée") {
                                return <td key={idx} className="af-mono">{formatDatePG(item[header])}</td>;
                              }
                              if (COLONNES_NUM.includes(header)) {
                                return <td key={idx} className="num af-mono">{item[header]}</td>;
                              }
                              return <td key={idx}>{item[header]}</td>;
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TableauAnalyseFaisabilite;